import { v4 as uuidv4 } from 'uuid';
import { ref } from 'vue';
import type { SnackBarOptions, SnackbarAction, UseSnackbar } from '@/types/composables/useSnackbar';

const snackbars: UseSnackbar['snackbars'] = ref([]);

const getSnackBarDefaultsByType = (
  type: SnackBarOptions['type']
): Required<Pick<SnackBarOptions, 'enabled' | 'timeout' | 'key' | 'color' | 'icon'>> => {
  const defaults = {
    enabled: true,
    timeout: 5000,
    icon: 'mdi-information-outline',
    key: uuidv4()
  };

  switch (type) {
    case 'error':
      return {
        ...defaults,
        color: 'alert-error',
        icon: 'mdi-alert-outline'
      };
    case 'attention':
      return {
        ...defaults,
        color: 'warning'
      };
    case 'success':
      return {
        ...defaults,
        color: 'success',
        icon: 'mdi-check-circle-outline'
      };
    case 'info':
      return {
        ...defaults,
        color: 'info'
      };
    default:
      return { ...defaults, color: 'info' };
  }
};

const getSnackBarActionDefaultsByType = (
  type: SnackBarOptions['type']
): Partial<SnackbarAction> => {
  const defaults = {
    key: uuidv4()
  };

  switch (type) {
    case 'error':
      return {
        ...defaults,
        color: 'error-graphic'
      };
    case 'attention':
      return {
        ...defaults,
        color: 'warning-graphic'
      };
    case 'success':
      return {
        ...defaults,
        color: 'success-graphic'
      };
    case 'info':
      return {
        ...defaults,
        color: 'info-graphic'
      };
    default:
      return { ...defaults, color: 'on-alert' };
  }
};

const removeSnackbar: UseSnackbar['removeSnackbar'] = (key) => {
  console.debug(`Removed snackbar with key: ${key}`);
  snackbars.value = snackbars.value.filter((snack) => snack.key !== key);
};

const addSnackbar: UseSnackbar['addSnackbar'] = (snackBar) => {
  const copy: Omit<SnackBarOptions, 'key'> & Required<Pick<SnackBarOptions, 'key'>> = {
    // Set up the defaults
    ...getSnackBarDefaultsByType(snackBar.type),
    vertical: !!snackBar.actions?.length,
    ...snackBar,
    actions: snackBar.actions?.map((action) => ({
      ...getSnackBarActionDefaultsByType(snackBar.type),
      ...action
    }))
  };

  if (!copy.timeout) {
    if (!copy.actions?.length) {
      throw new Error('Snackbar without actions must have a timeout!');
    } else if (!copy.key) {
      throw new Error(
        'Snackbar without timeout and with actions must have a key to be able to remove it later!'
      );
    }
  }

  // Prevent duplicate snackbars
  snackbars.value = snackbars.value.filter(({ message }) => message !== copy.message);
  console.debug('Pushed new snackbar: ', copy);
  snackbars.value = [...snackbars.value, copy];
  return copy.key;
};
export default function useSnackbar(): UseSnackbar {
  return { snackbars, addSnackbar, removeSnackbar };
}
